// SettingsVehicleDialog - MODULE
// ***********************************************************************************************************************
import * as React from "react";
import Datetime from "react-datetime";
import * as store from "../../framework/customStore";
import * as baseService from "../../services/baseService";
import * as storeActions from "../../models/store/storeActions";
import * as vehicleService from "../../services/vehicleService";
import { PropertyDialog } from "../framework/dialog";
import { Base } from "../../framework/base";
import { SaveData } from "../../framework/saveData";
import { CheckBox } from "../framework/checkbox";
import { ConfirmationDialogType } from "../../models/store/storeTypes";
import { IVehicleEdit } from "../../models/vehicle/vehicleEdit";
import { IIdTitle, IdTitle } from "../../models/common/idTitle";
import { SettingsVehicleDialogEmployeeList } from "./settingsVehicleDialogEmployeeList";
import { SettingsVehicleDialogToolList } from "./settingsVehicleDialogToolList";
import { IVehicleEditItemIntegration, VehicleEditItemIntegration } from "../../models/vehicle/vehicleEditItemIntegration";
import { ImageSelector } from "../framework/imageSelector";
import { IdTitleDescription, IIdTitleDescription } from "../../models/common/idTitleDescription";
import { t } from "i18next";
import { FuelType } from "../../models/vehicle/fuelType";
import { FlespiDeviceType } from "../../models/vehicle/flespiDeviceType";

// SettingsVehicleDialog
// ***********************************************************************************************************************
export interface ISettingsVehicleDialogProp {
    classes?: string;
    editItem: IVehicleEdit;
    onOk: () => void;
    onCancel: () => void;
}

export interface ISettingsVehicleDialogState {
    costCenters: IIdTitleDescription[];
    vehicleGroupId: string;
    costCenterId: string;
    registerNumber: string;
    brand: string;
    picture: string;
    pictureFile: File;
    removePicture: boolean;
    abbreviation: string;
    comment: string;
    activeState: number;
    inspectionDateStr: string;
    code: string;
    employees: IIdTitle[];
    removedEmployeeIds: string[];
    savedEmployeeIds: string[];
    tools: IIdTitle[];
    removedToolIds: string[];
    savedToolIds: string[];
    selectedLocationIds: string[];
    integrations: IVehicleEditItemIntegration[];
    fuelType: FuelType;
    createToFlespi: boolean;
    flespiDeviceTypeId: number;
    telemetryIdentity: string;
}

export class SettingsVehicleDialog extends React.Component<ISettingsVehicleDialogProp, ISettingsVehicleDialogState> {
    private static orgStateHash: string = "";

    constructor(props: ISettingsVehicleDialogProp) {
        super(props);
        const vehicle = props.editItem.vehicle;
        const costCenters = props.editItem.costCenters.slice(0);
        costCenters.unshift(IdTitleDescription.createIdTitleDescription(Base.emptyGuid, "", ""));
        this.state = {
            costCenters: costCenters,
            vehicleGroupId: vehicle.vehicleGroupId,
            costCenterId: vehicle.costCenterId,
            registerNumber: vehicle.registerNumber,
            brand: vehicle.brand,
            picture: vehicle.picture,
            pictureFile: null,
            removePicture: false,
            abbreviation: vehicle.abbreviation,
            comment: vehicle.comment,
            activeState: vehicle.activeState,
            inspectionDateStr: vehicle.inspectionDate ? Base.utcTimeToDateStr(vehicle.inspectionDate) : "",
            code: vehicle.code,
            employees: vehicle.employees.slice(0),
            removedEmployeeIds: [],
            savedEmployeeIds: [],
            tools: vehicle.tools.slice(0),
            removedToolIds: [],
            savedToolIds: [],
            selectedLocationIds: [],
            integrations: vehicle.integrations.slice(0),
            fuelType: vehicle.fuelType,
            createToFlespi: false,
            flespiDeviceTypeId: 0,
            telemetryIdentity: ""
        };
        const saveData = SettingsVehicleDialog.getSaveDataFromState(props, this.state);
        SettingsVehicleDialog.orgStateHash = saveData.hash;
    }

    // #region General
    handleChange = (event) => {
        const target = event.target;
        const value: string = target.value;
        const name: string = target.name;
        if (name === "registerNumber") {
            this.setState({ registerNumber: value });
        } else if (name === "brand") {
            this.setState({ brand: value });
        } else if (name === "abbreviation") {
            this.setState({ abbreviation: value });
        } else if (name === "comment") {
            this.setState({ comment: value });
        } else if (name === "vehicleGroupId") {
            this.setState({ vehicleGroupId: value });
        } else if (name === "costCenterId") {
            this.setState({ costCenterId: value });
        } else if (name === "code") {
            this.setState({ code: value });
        } else if (name === "telemetryIdentity") {
            this.setState({ telemetryIdentity: value });
        } else if (name === "flespiDeviceTypeId") {
            const flespiDeviceTypeId = parseInt(value, 10);
            this.setState({ flespiDeviceTypeId: flespiDeviceTypeId });
        } else if (name === "fuelType") {
            const fuelTypeInt = parseInt(value, 10);
            let fuelType = FuelType.Unknown;
            if (Object.values(FuelType).includes(fuelTypeInt)) {
                fuelType = fuelTypeInt as FuelType;
            }
            this.setState({ fuelType: fuelType });
        }
    };

    handleActiveStateChange = (activeState: number) => {
        this.setState({ activeState: activeState });
    };

    handleInspectionDateChange = (value: moment.Moment | string) => {
        if (typeof value === "string") {
            if (!value) {
                this.setState({ inspectionDateStr: "" });
            }
        } else {
            this.setState({ inspectionDateStr: Base.utcTimeToDateStr(value) });
        }
    };

    handleInspectionDateBlur = (value: moment.Moment | string) => {
        let inspectionDateStr: string;
        if (typeof value === "string") {
            inspectionDateStr = Base.utcTimeToDateStr(value.toUtcDate().getTime());
        } else {
            inspectionDateStr = Base.utcTimeToDateStr(value);
        }
        this.setState({ inspectionDateStr: inspectionDateStr });
    };

    handleSetPicture = (file: File) => {
        if (Base.isNullOrUndefined(file)) return;
        void Base.blobToBase64(file).then(base64Image => {
            this.setState({
                pictureFile: file,
                picture: base64Image,
                removePicture: false
            });
        });
    };

    handleRemovePicture = () => {
        this.setState({
            pictureFile: null,
            picture: "",
            removePicture: true
        });
    };

    handleSetCreateToFlespi = (createToFlespi: boolean) => {
        this.setState({ createToFlespi: createToFlespi });
    };

    handleIntegrationActiveStateChange = (id: string, activeState: number) => {
        const integrations = this.state.integrations.slice(0);
        const integration = integrations.find(i => i.id === id);
        if (!integration) return;
        integration.activeState = activeState;
        this.setState({ integrations: integrations });
    };

    handleIntegrationIdChange = (id: string, event) => {
        const target = event.target;
        const value: string = target.value;
        const integrations = this.state.integrations.slice(0);
        const integration = integrations.find(i => i.id === id);
        if (!integration) return;
        integration.uniqueId = value;
        this.setState({ integrations: integrations });
    };
    // #endregion General

    // #region Employees
    handleEmployeeAdd = (id: string, title: string) => {
        const employees = this.state.employees.slice(0);
        const savedEmployeeIds = this.state.savedEmployeeIds.slice(0);
        const item = new IdTitle();
        item.id = id;
        item.title = title;
        item.isNew = true;
        employees.push(item);
        savedEmployeeIds.push(item.id);
        IdTitle.sortIdTitles(employees, "title", true);
        this.setState({ employees: employees, savedEmployeeIds: savedEmployeeIds });
    };

    handleEmployeeRemove = (id: string) => {
        if (!id) return;
        const item = this.state.employees.find(i => i.id === id);
        if (Base.isNullOrUndefined(item)) return;
        const removedEmployeeIds = this.state.removedEmployeeIds.filter(i => i !== id);
        if (!item.isNew) {
            removedEmployeeIds.push(id);
        }
        this.setState({ employees: this.state.employees.filter(i => i.id !== id), savedEmployeeIds: this.state.savedEmployeeIds.filter(i => i !== id), removedEmployeeIds: removedEmployeeIds });
    };
    // #endregion Employees

    // #region Tools
    handleToolAdd = (id: string, title: string) => {
        const tools = this.state.tools.slice(0);
        const savedToolIds = this.state.savedToolIds.slice(0);
        const item = new IdTitle();
        item.id = id;
        item.title = title;
        item.isNew = true;
        tools.push(item);
        savedToolIds.push(item.id);
        IdTitle.sortIdTitles(tools, "title", true);
        this.setState({ tools: tools, savedToolIds: savedToolIds });
    };

    handleToolRemove = (id: string) => {
        if (!id) return;
        const item = this.state.tools.find(i => i.id === id);
        if (Base.isNullOrUndefined(item)) return;
        const removedToolIds = this.state.removedToolIds.filter(i => i !== id);
        if (!item.isNew) {
            removedToolIds.push(id);
        }
        this.setState({ tools: this.state.tools.filter(i => i.id !== id), savedToolIds: this.state.savedToolIds.filter(i => i !== id), removedToolIds: removedToolIds });
    };
    // #endregion Tools

    private static validate = (state: ISettingsVehicleDialogState): boolean => {
        if (!state.registerNumber) {
            store.customStore.dispatch(storeActions.showErrorMessage(t("common.registerNumberMustBeDefined")));
            return false;
        }
        return true;
    };

    private static getSaveDataFromState = (props: ISettingsVehicleDialogProp, state: ISettingsVehicleDialogState): SaveData => {
        const data = new SaveData();
        const vehicle = props.editItem.vehicle;
        // Common
        data.append("id", vehicle.id);
        data.append("rowId", vehicle.rowId);
        // General
        data.append("vehicleGroupId", state.vehicleGroupId);
        data.append("costCenterId", state.costCenterId !== Base.emptyGuid ? state.costCenterId : String(null));
        data.append("registerNumber", state.registerNumber);
        data.append("brand", state.brand);
        data.append("abbreviation", state.abbreviation);
        data.append("comment", state.comment);
        data.append("activeState", state.activeState.toString(10));
        data.append("inspectionDate", state.inspectionDateStr);
        data.append("code", state.code);
        data.append("fuelType", (state.fuelType as number).toString(10));

        if (!Base.isNullOrUndefined(state.pictureFile)) {
            data.append("picture[]", state.pictureFile, state.pictureFile.name);
        }
        data.append("removePicture", state.removePicture ? "1" : "0");
        // Employees
        data.append("addedEmployeeIds", JSON.stringify(state.employees.reduce((result: string[], i) => {
            if (state.savedEmployeeIds.indexOf(i.id) >= 0) {
                result.push(i.id);
            }
            return result;
        }, [])));
        data.append("removedEmployeeIds", JSON.stringify(state.removedEmployeeIds));
        // tools
        data.append("addedToolIds", JSON.stringify(state.tools.reduce((result: string[], i) => {
            if (state.savedToolIds.indexOf(i.id) >= 0) {
                result.push(i.id);
            }
            return result;
        }, [])));
        data.append("removedToolIds", JSON.stringify(state.removedToolIds));
        // Integrations
        data.append("integrations", JSON.stringify(state.integrations.map(i => new VehicleEditItemIntegration(i))));
        data.append("createToFlespi", state.createToFlespi ? "1" : "0");
        if (state.createToFlespi) {
            data.append("telemetryIdentity", state.telemetryIdentity);
            data.append("flespiDeviceTypeId", state.flespiDeviceTypeId.toString(10));
        }
        return data;
    };

    saveEditItem = () => {
        if (!SettingsVehicleDialog.validate(this.state)) return;
        const saveData = SettingsVehicleDialog.getSaveDataFromState(this.props, this.state);
        if (Base.isNullOrUndefined(saveData)) return;
        // Call server
        store.customStore.dispatch(storeActions.fetchStart());
        vehicleService.saveVehicleEdit(saveData.formData)
            .then(success => {
                store.customStore.dispatch(storeActions.showSuccessMessage(success.message));
                this.props.onOk();
            })
            .catch(error => {
                store.customStore.dispatch(storeActions.showErrorMessage(baseService.getErrorMessageFromError(error)));
                return null;
            })
            .finally(() => store.customStore.dispatch(storeActions.fetchEnd()));
    };

    handleOkClick = () => {
        this.saveEditItem();
    };

    handleSetSelectedLocationIds = (ids: string[]) => {
        this.setState({ selectedLocationIds: ids });
    };

    handleCancelClick = () => {
        const saveData = SettingsVehicleDialog.getSaveDataFromState(this.props, this.state);
        if (!Base.isNullOrUndefined(saveData) && saveData.hash !== SettingsVehicleDialog.orgStateHash) {
            store.customStore.dispatch(storeActions.setConfirmation(ConfirmationDialogType.Warning, t("common.warning"), t("common.youHaveNotSavedChangesDoYouWantToSaveChanges"),
                () => {
                    store.customStore.dispatch(storeActions.clearConfirmation());
                    this.saveEditItem();
                },
                () => {
                    store.customStore.dispatch(storeActions.clearConfirmation());
                    this.props.onCancel();
                },
                () => {
                    store.customStore.dispatch(storeActions.clearConfirmation());
                }));
        } else {
            this.props.onCancel();
        }
    };

    // #region Integration checkbox activity helpers

    // Check that if Mapon integration or Flespi integration is active and disables Mapon or Flespi
    // If same integration type is available multiple times, inactivates other (multiple Mapon integrations)
    isIntegrationActivityChangeAllowed = (serviceName, integrationId) => {
        if (serviceName === "Mapon") {
            // Check if Flespi is already active for this vehicle, if yes, not allowed to change integration activity
            return !this.hasActiveIntegrationForService("Flespi") &&
                !this.hasActiveIntegrationForServiceButWithDifferentId("Mapon", integrationId);
        } else if (serviceName === "Flespi") {
            // Check if Mapon is already active for this vehicle, if yes, not allowed to change integration activity
            return !this.hasActiveIntegrationForService("Mapon") &&
                !this.hasActiveIntegrationForServiceButWithDifferentId("Flespi", integrationId);
        } else {
            // For other integrations we allow changing Integration Activity state (enables integration checkbox)
            return true;
        }
    };

    // Has integration service link active or inactive for service
    hasIntegrationForService = (serviceName) => {
        const integrations = this.state.integrations.slice(0);
        const integration = integrations.find(i => i.serviceName === serviceName);
        return !integration ? false : true;
    };

    // Has integration service link active for service
    hasActiveIntegrationForService = (serviceName) => {
        const integrations = this.state.integrations.slice(0);
        const integration = integrations.find(i => i.activeState === 1 && i.serviceName === serviceName);
        return !integration ? false : true;
    };
    hasActiveIntegrationForServiceButWithDifferentId = (serviceName, integrationId) => {
        const integrations = this.state.integrations.slice(0);
        const integration = integrations.find(i => i.activeState === 1 && i.serviceName === serviceName && i.id !== integrationId);
        return !integration ? false : true;
    };
    // #endregion


    render() {
        const props = this.props;
        const state = this.state;
        const editItem = props.editItem;
        const vehicle = editItem.vehicle;
        const dialogClasses = "settings vehicle px800" + (props.classes ? " " + props.classes : "");
        const activeIntegrationsExist = state.integrations.filter(i => i.activeState > 0 && !!i.uniqueId).length > 0;
        return (
            <div>
                <PropertyDialog
                    classes={dialogClasses}
                    initialHeightOffset={activeIntegrationsExist ? 200 : 0}
                    title={t("vehicle.vehicle") + " - " + (!vehicle.isNew() ? vehicle.number.toString(10) + " " + vehicle.registerNumber : t("common.new"))}
                    show={true}
                    body={
                        <div>
                            <div className="row">
                                <div className="col-3">
                                    <ImageSelector
                                        fileInputName="picture"
                                        image={state.picture}
                                        aspectRatio={null}
                                        onChange={this.handleSetPicture}
                                        onClear={this.handleRemovePicture}
                                        onError={(message) => { store.customStore.dispatch(storeActions.showErrorMessage(message)); }}
                                    />
                                </div>
                                <div className="col-9">
                                    <div className="row">
                                        <div className="col-2">
                                            <div className="form-group required">
                                                <label className="control-label smallFont">{t("common.number")}</label>
                                                <input type="text" className="form-control" name="number" title={t("common.number")} value={vehicle.isNew() ? t("common.new") : vehicle.number.toString(10)} readOnly={true} disabled={true} />
                                            </div>
                                        </div>
                                        <div className="col-4">
                                            <div className="form-group required">
                                                <label className="control-label smallFont">{t("common.registerNumber")}</label>
                                                <input type="text" className="form-control" name="registerNumber" title={t("common.registerNumber")} value={state.registerNumber} onChange={this.handleChange} maxLength={50} />
                                            </div>
                                        </div>
                                        <div className="col-4">
                                            <div className="form-group required">
                                                <label className="control-label smallFont">{t("vehicle.group")}</label>
                                                <select className="form-control" name="vehicleGroupId" title={t("vehicle.group")} value={state.vehicleGroupId} onChange={this.handleChange}>
                                                    {editItem.vehicleGroups.map((vehicleGroup) =>
                                                        <option key={vehicleGroup.id} value={vehicleGroup.id}>{vehicleGroup.title}</option>
                                                    )}
                                                </select>
                                            </div>
                                        </div>
                                        <div className="col-2">
                                            <div className="form-group">
                                                <label className="control-label smallFont">{t("common.abbreviation")}</label>
                                                <input type="text" className="form-control" name="abbreviation" title={t("common.abbreviation")} value={state.abbreviation} onChange={this.handleChange} maxLength={3} />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="row">
                                        <div className="col-3">
                                            <div className="form-group">
                                                <label className="control-label smallFont">{t("common.nextVehicleInspection")}</label>
                                                <Datetime
                                                    locale={appConfig.culture}
                                                    className={"roWhite"}
                                                    value={state.inspectionDateStr}
                                                    dateFormat={"D.M.YYYY"}
                                                    timeFormat={false}
                                                    closeOnSelect={true}
                                                    inputProps={{ maxLength: 10 }}
                                                    onChange={this.handleInspectionDateChange}
                                                    onClose={this.handleInspectionDateBlur}
                                                />
                                            </div>
                                        </div>
                                        <div className="col-3">
                                            <div className="form-group">
                                                <label className="control-label smallFont">&nbsp;</label>
                                                <div>
                                                    <CheckBox
                                                        title={t("common.inUse")}
                                                        enabled={true}
                                                        checked={state.activeState > 0}
                                                        onCheckboxClickBoolean={(checked: boolean) => { this.handleActiveStateChange(checked ? 1 : 0); }}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                        <div className="col-6">
                                            <div className="form-group">
                                                <label className="control-label smallFont">{t("common.brand")}</label>
                                                <input type="text" className="form-control" name="brand" title={t("common.brand")} value={state.brand} onChange={this.handleChange} maxLength={50} />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="row">
                                <div className="col-12">
                                    <div className="form-group">
                                        <label className="control-label smallFont">{t("common.comment")}</label>
                                        <textarea className="form-control" name="comment" title={t("common.comment")} value={state.comment} onChange={this.handleChange} maxLength={1000} />
                                    </div>
                                </div>
                            </div>
                            <div className="row">
                                <div className="col-6">
                                    <div className="form-group">
                                        <label className="control-label smallFont">{t("costCenter.costCenter")}</label>
                                        <select className="form-control" name="costCenterId" title={t("costCenter.costCenter")} value={state.costCenterId} onChange={this.handleChange}>
                                            {state.costCenters.map((costCenter) =>
                                                <option key={costCenter.id} value={costCenter.id}>{costCenter.title}</option>
                                            )}
                                        </select>
                                    </div>
                                </div>
                                <div className="col-6">
                                    <div className="form-group">
                                        <label className="control-label smallFont wrap">{t("costCenter.code")}</label>
                                        <input type="text" className="form-control" name="code" title={t("common.code")} value={state.code} onChange={this.handleChange} />
                                    </div>
                                </div>
                            </div>
                            <div className="row">
                                <div className="col-6">
                                    <div className="form-group">
                                        <label className="control-label smallFont wrap">{t("common.fuelTypes.fuelTypeTitle")}</label>
                                        <select className="form-control" name="fuelType" title={t("common.fuelTypes.fuelTypeTitle")}
                                            value={state.fuelType as number}
                                            onChange={this.handleChange}
                                        >
                                            {Object.values(FuelType).filter(value => typeof value === "number").map((o) =>
                                                <option key={o} value={o}>{t(`common.fuelTypes.fuelType.${FuelType[o]}`)}</option>
                                            )}
                                        </select>
                                    </div>
                                </div>
                            </div>

                            <label className="control-label listTitle">{t("common.integrations")}</label>
                            {state.integrations.map((integration) =>
                                <div key={integration.id} className="row">
                                    <div className="col-3">
                                        <div className="form-group">
                                            <label className="control-label smallFont">&nbsp;</label>
                                            <div>
                                                <CheckBox
                                                    title={integration.serviceName}
                                                    enabled={this.isIntegrationActivityChangeAllowed(integration.serviceName, integration.id)}
                                                    checked={integration.activeState > 0}
                                                    onCheckboxClickBoolean={(checked: boolean) => { this.handleIntegrationActiveStateChange(integration.id, checked ? 1 : 0); }}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-3">
                                        <div className="form-group">
                                            <label className="control-label smallFont">{t("common.code")}</label>
                                            <input type="text" className="form-control" name="brand" title={integration.serviceName} value={integration.uniqueId} onChange={(e) => this.handleIntegrationIdChange(integration.id, e)} maxLength={250} />
                                        </div>
                                    </div>
                                </div>
                            )}
                            {!this.hasIntegrationForService("Flespi") &&
                                <div>
                                    <label className="control-label smallFont">&nbsp;</label>
                                    <div>
                                        <CheckBox
                                            title={t("vehicleIntegrations.addToIntegrationServiceTitle")}
                                            enabled={!this.hasActiveIntegrationForService("Flespi")}
                                            checked={state.createToFlespi}
                                            onCheckboxClickBoolean={(checked: boolean) => { this.handleSetCreateToFlespi(checked); }}
                                        />
                                        {state.createToFlespi &&
                                            <div>
                                                <div className="col-10">
                                                    <div className="row">
                                                        <div className="col-6">
                                                            <div className="form-group">
                                                                <label className="control-label smallFont">{t("vehicleIntegrations.telemetryIdentity")}</label>
                                                                <input type="text"
                                                                    className="form-control"
                                                                    name="telemetryIdentity"
                                                                    title="telemetryIdentity"
                                                                    value={state.telemetryIdentity}
                                                                    onChange={this.handleChange}
                                                                />
                                                            </div>
                                                        </div>
                                                        <div className="col-4">
                                                            <div className="form-group">
                                                                <label className="control-label smallFont wrap">{t("vehicleIntegrations.telemetryDeviceType")}</label>
                                                                <select className="form-control" name="flespiDeviceTypeId" title={t("vehicleIntegrations.telemetryDeviceType")}
                                                                    value={state.flespiDeviceTypeId}
                                                                    onChange={this.handleChange}
                                                                >
                                                                    {Object.values(FlespiDeviceType)
                                                                        .filter(value => typeof value === "number" && value !== FlespiDeviceType.Unknown)
                                                                        .map((o) => {
                                                                            const name = FlespiDeviceType[o as number];
                                                                            const displayName = name.replace(/_/g, " ");
                                                                            return { value: o, displayName };
                                                                        })
                                                                        .sort((a, b) => a.displayName.localeCompare(b.displayName))
                                                                        .map(({ value, displayName }) => (
                                                                            <option key={value} value={value}>
                                                                                {displayName}
                                                                            </option>
                                                                        ))
                                                                    }
                                                                </select>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        }
                                    </div>
                                </div>
                            }

                            <div>
                                <div>
                                    <SettingsVehicleDialogEmployeeList
                                        title={t("employee.employees")}
                                        items={state.employees}
                                        onRemoveEmployee={this.handleEmployeeRemove}
                                        onAddEmployee={this.handleEmployeeAdd}
                                    />
                                </div>
                            </div>
                            <div>
                                <div>
                                    <SettingsVehicleDialogToolList
                                        title={t("tool.s")}
                                        items={state.tools}
                                        onRemoveTool={this.handleToolRemove}
                                        onAddTool={this.handleToolAdd}
                                    />
                                </div>
                            </div>
                        </div>
                    }
                    buttons={[
                        { title: t("common.save"), classes: "btn-primary", enabled: true, onClick: this.handleOkClick },
                    ]}
                    onClose={this.handleCancelClick}
                />
            </div>
        );
    }
}
